import { motion, useReducedMotion } from "motion/react";
import { APPLICATION_URL, boats, finalCtaCopy, gameCopy } from "../../content";
import { STATIONS, useGame } from "../../game/GameContext";
import { Button } from "../ui/Button";
import { BoatLine } from "../art/BoatLine";
import { WaveRule } from "../art/WaveRule";

const easeOut = [0.16, 1, 0.3, 1] as const;

/** The big finish: a boarding ticket, stamped with how you actually sailed. */
export function FinalCta() {
  const reduce = useReducedMotion();
  const { boatId, crew, stampCount } = useGame();
  const arrival = gameCopy.arrival;
  const vessel = boats.find((b) => b.id === boatId)?.label ?? arrival.tbd;
  const crewLabel = crew.length > 0 ? `you + ${crew.length}` : arrival.solo;
  const fullVoyage = stampCount === STATIONS.length;

  return (
    <section id="apply" aria-label="Apply to Same Boat" className="chart-grid relative border-y border-line bg-paper-deep py-16 md:py-24">
      <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center px-5 md:px-10">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 32, rotate: -2.2 }}
          whileInView={{ opacity: 1, y: 0, rotate: -0.8 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: easeOut }}
          className="flex w-full max-w-[1150px] rounded-md border-2 border-ink bg-foam shadow-lift"
        >
          {/* ticket main */}
          <div className="min-w-0 flex-1 p-10 md:p-14">
            <h2 className="display-type-wide text-[clamp(2rem,4.6vw,4.6rem)] text-ink">
              {finalCtaCopy.titleLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </h2>
            {/* the ONE signal-variant button on the whole page */}
            <Button variant="signal" size="lg" href={APPLICATION_URL} className="mt-10">
              {finalCtaCopy.cta}
            </Button>
          </div>

          {/* tear-off stub: the voyage manifest, filled in by how you played */}
          <div className="hidden w-52 shrink-0 flex-col items-center justify-between gap-6 border-l-2 border-dashed border-ink p-7 sm:flex">
            <BoatLine className="w-24 text-ink" />
            <dl className="w-full space-y-3 font-mono text-xs text-ink">
              <div>
                <dt className="text-muted uppercase">{arrival.vessel}</dt>
                <dd className="mt-0.5 font-semibold">{vessel}</dd>
              </div>
              <div>
                <dt className="text-muted uppercase">{arrival.crew}</dt>
                <dd className="mt-0.5 font-semibold">{crewLabel}</dd>
              </div>
              <div>
                <dt className="text-muted uppercase">{arrival.ports}</dt>
                <dd className="mt-0.5 font-semibold">
                  {stampCount}/{STATIONS.length}
                </dd>
              </div>
            </dl>
            {stampCount > 0 && (
              <p className="text-center font-mono text-[10px] leading-relaxed text-muted">
                {fullVoyage ? arrival.completeVoyage : arrival.incompleteVoyage}
              </p>
            )}
            <WaveRule className="text-ink" />
          </div>
        </motion.div>

        <motion.p
          initial={reduce ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.2, ease: easeOut }}
          className="hand-note mt-9 rotate-[-1.5deg] text-center text-ink-soft"
        >
          {finalCtaCopy.note}
        </motion.p>
      </div>
    </section>
  );
}
